/**
 * @typedef {import('../types/support').JiraAdfNode} JiraAdfNode
 * @typedef {import('../types/support').JiraStatusCategoryKey} JiraStatusCategoryKey
 * @typedef {import('../types/support').PriorityPt} PriorityPt
 * @typedef {import('../types/support').SupportStatusFilter} SupportStatusFilter
 * @typedef {import('../types/support').SupportIssue} SupportIssue
 * @typedef {import('../types/support').SupportAttachment} SupportAttachment
 * @typedef {import('../types/support').SupportComment} SupportComment
 * @typedef {import('../types/support').SupportAttachmentViewModel} SupportAttachmentViewModel
 * @typedef {import('../types/support').SupportCommentViewModel} SupportCommentViewModel
 * @typedef {import('../types/support').SupportIssueViewModel} SupportIssueViewModel
 */

/**
 * Extrai texto plano recursivamente de um nó ADF.
 * @param {JiraAdfNode | undefined | null} node
 * @returns {string}
 */
export function extractAdfText(node) {
  if (!node) return '';
  if (typeof node.text === 'string') return node.text;
  if (Array.isArray(node.content)) {
    const raw = node.content.map(extractAdfText).join(' ');
    return raw.replace(/\s+/g, ' ').trim();
  }
  return '';
}

/**
 * Normaliza string: remove acentos, lowercased.
 * @param {string | undefined | null} str
 * @returns {string}
 */
function normalize(str) {
  if (!str) return '';
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * @param {string | undefined | null} name
 * @returns {PriorityPt | 'Não definida'}
 */
export function mapPriorityToPt(name) {
  if (!name) return 'Não definida';
  const n = normalize(name.trim());

  if (n === '1' || n === 'highest') return 'Muito Alto';
  if (n === '2' || n === 'high') return 'Alto';
  if (n === '3' || n === 'medium') return 'Médio';
  if (n === '4' || n === 'low') return 'Baixo';
  if (n === '5' || n === 'lowest') return 'Muito Baixo';

  if (n === 'muito alto' || n === 'muito-alto' || n === 'muitoalto') return 'Muito Alto';
  if (n === 'alto' || n === 'alta') return 'Alto';
  if (n === 'medio' || n === 'media') return 'Médio';
  if (n === 'baixo' || n === 'baixa') return 'Baixo';
  if (n === 'muito baixo' || n === 'muito-baixo' || n === 'muitobaixo') return 'Muito Baixo';

  return 'Não definida';
}

/**
 * @param {PriorityPt} priority
 * @returns {string}
 */
export function mapPriorityPtToApiId(priority) {
  switch (priority) {
    case 'Muito Alto': return '1';
    case 'Alto': return '2';
    case 'Médio': return '3';
    case 'Baixo': return '4';
    case 'Muito Baixo': return '5';
    default: return '3';
  }
}

/**
 * @param {JiraStatusCategoryKey | undefined} key
 * @returns {SupportStatusFilter}
 */
export function mapStatusCategoryToCard(key) {
  if (key === 'done') return 'Concluídos';
  if (key === 'indeterminate') return 'Em andamento';
  return 'Em espera';
}

/**
 * @param {string | undefined | null} statusName
 * @returns {JiraStatusCategoryKey}
 */
export function inferStatusCategoryKey(statusName) {
  const n = normalize(statusName);
  if (/done|conclu|finaliz|fechad/.test(n)) return 'done';
  if (/andamento|in progress|progresso|indeterminate/.test(n)) return 'indeterminate';
  return 'new';
}

/**
 * @param {SupportIssue} issue
 * @returns {{ statusCategoryKey: JiraStatusCategoryKey; statusName: string }}
 */
export function resolveIssueStatus(issue) {
  const statusName = issue.status_name || issue.status || 'Sem status';
  const statusCategoryKey = issue.status_category_key || inferStatusCategoryKey(statusName);
  return { statusCategoryKey, statusName };
}

/**
 * @param {SupportAttachment[] | null | undefined} items
 * @returns {SupportAttachmentViewModel[]}
 */
export function mapSupportAttachmentsToUi(items) {
  if (!items || !items.length) return [];
  return items.map((a) => ({
    id: a.external_id != null ? String(a.external_id) : undefined,
    filename: a.filename ?? undefined,
    content: a.url ?? undefined,
  }));
}

/**
 * @param {SupportComment[] | null | undefined} comments
 * @returns {SupportCommentViewModel[]}
 */
export function normalizeSupportCommentsList(comments) {
  if (!comments || !comments.length) return [];

  const mapped = comments.map((c) => {
    const rawAdf = /** @type {JiraAdfNode | undefined} */ (c.body);
    const bodyAdf = rawAdf;
    const bodyText = extractAdfText(rawAdf);
    const authorName = c.author?.name || c.author?.email || '—';
    const dateStr = c.created_at || c.created || '';
    const id = c.external_id != null
      ? String(c.external_id)
      : `${dateStr}-${authorName}`;

    return /** @type {SupportCommentViewModel} */ ({
      id,
      authorName,
      createdAt: c.created_at || c.created || undefined,
      bodyAdf,
      bodyText,
      attachments: mapSupportAttachmentsToUi(c.attachments),
    });
  });

  return mapped.sort((a, b) => {
    const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return da - db;
  });
}

/**
 * @param {SupportIssue} issue
 * @returns {SupportIssueViewModel | null}
 */
export function supportIssueToViewModel(issue) {
  if (!issue.external_id) return null;
  const issueExternalId = String(issue.external_id).trim();
  if (!issueExternalId) return null;

  const key = issue.external_key || issueExternalId;
  const summary = issue.summary || '(Sem título)';
  const title = `${key} - ${summary}`;
  const descriptionAdf = /** @type {JiraAdfNode | undefined} */ (issue.description);
  const content = extractAdfText(descriptionAdf) || 'Sem descrição';

  const authorName = issue.author?.name || issue.author?.email || 'Autor não informado';
  const authorEmail = issue.author?.email || 'E-mail não informado';

  const { statusCategoryKey, statusName } = resolveIssueStatus(issue);
  const priority = mapPriorityToPt(issue.priority || issue.priority_name);

  const commentsList = normalizeSupportCommentsList(issue.comments);
  const attachments = mapSupportAttachmentsToUi(issue.attachments);

  return {
    issueExternalId,
    id: issueExternalId,
    key,
    title,
    content,
    authorName,
    authorEmail,
    descriptionAdf,
    statusCategoryKey,
    statusName,
    priority,
    comments: commentsList.length,
    createdAt: issue.created_at || undefined,
    updatedAt: issue.updated_at || undefined,
    commentsList,
    attachments,
  };
}
